import isEmpty from "lodash/isEmpty";
import noop from "lodash/noop";
import React, { useEffect } from "react";

import styled from "veneer/styled";
import Typography from "veneer/Typography";

import { List } from "cortex/ui/src/ui/list";

import { CustomParamField } from "./CustomParamField";
import Overrides from "./Overrides";
import ParamUtils, { Priority } from "./utils";

interface CustomParamValue {
  errors?: string[];
  hasOverride: boolean;
  isDuplicate: boolean;
  isLegacy: boolean;
  isOverriding: boolean;
  k: string;
  v: unknown;
}

interface NetworkParam {
  name: string;
  value: unknown;
}

interface CustomParamFormFieldProps {
  index: number;
  legacyParams?: Set<string>;
  network: {
    id: number;
  };
  networkParams: NetworkParam[];
  onChange: (value: CustomParamValue) => void;
  onOverride?: (index: number, value: boolean) => void;
  onRemove?: () => void;
  params: CustomParamValue[];
  priority: Priority;
  readOnly?: boolean;
  value: CustomParamValue;
}

const FieldContainer = styled("div")({
  marginBottom: 15,
});

function hasDuplicateParam(params: CustomParamValue[], key: string) {
  if (!key) {
    return false;
  }
  const matchingKeys = params.filter(
    ({ k: paramKey }) => paramKey === key,
  ).length;
  return matchingKeys > 1;
}

const CustomParamFormField = ({
  index,
  legacyParams = new Set(),
  network,
  networkParams,
  onChange,
  onOverride = () => {},
  onRemove = noop,
  params,
  priority,
  readOnly = false,
  value,
}: CustomParamFormFieldProps) => {
  const { k, errors = [], hasOverride, isOverriding } = value;
  const hasNetworkLevel = ParamUtils.hasNetworkLevelParam(k, networkParams);
  const hasErrors = errors.length > 0;

  useEffect(() => {
    if (hasOverride) {
      onOverride(index, isOverriding);
    }
  }, []);

  const hasDuplicate = (key: string): boolean => {
    return hasDuplicateParam(params, key);
  };

  return (
    <FieldContainer className={!isEmpty(errors) ? "has-error" : ""}>
      <CustomParamField
        field={{ value }}
        handleChange={onChange}
        hasDuplicate={hasDuplicate}
        legacyParams={legacyParams}
        onRemove={onRemove}
        readOnly={readOnly}
      />
      {hasOverride && (
        <Overrides.Toggle
          defaultValue={isOverriding}
          onChange={(isEnabled) => onOverride(index, isEnabled)}
        >
          <Overrides.Label
            hasDefaultValue={false}
            hasNetworkLevel={hasNetworkLevel}
            networkId={network.id}
            priority={priority}
          />
        </Overrides.Toggle>
      )}
      {hasErrors && (
        <Typography component="div" variant="caption">
          <List items={errors} />
        </Typography>
      )}
    </FieldContainer>
  );
};

export { CustomParamFormField, hasDuplicateParam };
